import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Tabs, Tab, Paper, Button, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip,
  CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, InputAdornment, Stack, Divider, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import ClassIcon from '@mui/icons-material/Class';
import TuneIcon from '@mui/icons-material/Tune';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PercentIcon from '@mui/icons-material/Percent';
import { motion } from 'framer-motion';

import {
  fetchFeeCategories,
  createFeeCategory,
  updateFeeCategory,
  deleteFeeCategory,
  fetchDefaultRules,
  saveDefaultRules,
  fetchClassRules,
  saveClassRules,
  fetchClassDiscounts,
  saveClassDiscounts,
  fetchStudentFees,
  saveStudentFees,
  fetchStudentDiscounts,
  saveStudentDiscounts,
  fetchDiscountedStudents,
  clearStudentFees,
} from '../slices/feesManagementSlice';
import { fetchClasses } from '../slices/classesSlice';
import { fetchSchoolYears } from '../slices/schoolYearsSlice';
import axios from '../api/axiosInstance';

const TABS = [
  { label: 'أنواع الرسوم', icon: <CategoryIcon fontSize="small" /> },
  { label: 'الرسوم الافتراضية', icon: <TuneIcon fontSize="small" /> },
  { label: 'رسوم الصفوف', icon: <ClassIcon fontSize="small" /> },
  { label: 'رسوم الطلاب', icon: <PersonIcon fontSize="small" /> },
  { label: 'الطلاب ذوو الخصم', icon: <PercentIcon fontSize="small" /> },
];

const DISCOUNT_REASONS = [
  { value: 'financial_hardship', label: 'حالة مادية صعبة' },
  { value: 'siblings', label: 'خصم إخوة' },
  { value: 'staff_child', label: 'أبناء موظفين' },
  { value: 'scholarship', label: 'منحة دراسية' },
  { value: 'social', label: 'حالة اجتماعية' },
  { value: 'other', label: 'أخرى' },
];

const reasonLabel = (value) => DISCOUNT_REASONS.find((r) => r.value === value)?.label || '';

const currency = (v) =>
  new Intl.NumberFormat('ar-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, numberingSystem: 'latn' }).format(v || 0);

// 🎨 Design System tokens
const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';

// Shared card style for panels/tables
const cardSx = {
  borderRadius: 3,
  bgcolor: '#fff',
  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
  border: '1px solid #E5E7EB',
};

// Shared primary contained button style
const primaryBtnSx = {
  bgcolor: '#4338CA',
  color: '#fff',
  borderRadius: 2.5,
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: FONT,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
};

// Shared table header cell style
const headCellSx = {
  bgcolor: '#F1F5F9',
  fontWeight: 700,
  color: '#1E1B4B',
  fontFamily: FONT,
};

// ─── Tab 0 – Fee Categories ──────────────────────────────────────────────────

function CategoriesTab() {
  const dispatch = useDispatch();
  const { categories, loading } = useSelector((s) => s.feesManagement);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [nameError, setNameError] = useState('');

  const openAdd = () => { setNameInput(''); setNameError(''); setAddOpen(true); };
  const openEdit = (cat) => { setEditTarget(cat); setNameInput(cat.name); setNameError(''); };
  const closeDialogs = () => { setAddOpen(false); setEditTarget(null); };

  const handleSave = async () => {
    if (!nameInput.trim()) { setNameError('اسم الرسوم مطلوب'); return; }
    if (editTarget) {
      await dispatch(updateFeeCategory({ id: editTarget.id, data: { name: nameInput.trim() } }));
    } else {
      await dispatch(createFeeCategory({ name: nameInput.trim() }));
    }
    closeDialogs();
  };

  const handleToggleActive = async (cat) => {
    await dispatch(updateFeeCategory({ id: cat.id, data: { is_active: !cat.is_active } }));
    dispatch(fetchFeeCategories());
  };

  const handleDelete = async () => {
    await dispatch(deleteFeeCategory(deleteConfirm.id));
    setDeleteConfirm(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h6" fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: FONT }}>أنواع الرسوم المدرسية</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}>
          إضافة نوع رسوم
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
      ) : categories.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2.5, fontFamily: FONT }}>لا توجد أنواع رسوم. أضف نوعاً جديداً للبدء.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>#</TableCell>
                <TableCell sx={headCellSx}>اسم الرسوم</TableCell>
                <TableCell align="center" sx={headCellSx}>الحالة</TableCell>
                <TableCell align="center" sx={headCellSx}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...categories].sort((a, b) => a.order - b.order).map((cat, idx) => (
                <TableRow key={cat.id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6B7280', fontFamily: FONT }}>
                      <DragIndicatorIcon fontSize="small" />
                      {idx + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{cat.name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={cat.is_active ? 'مفعّل' : 'معطّل'}
                      color={cat.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(cat)}
                      icon={cat.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      sx={{ fontWeight: 700, fontFamily: FONT }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="تعديل">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(cat)}
                        sx={{ color: '#D97706', '&:hover': { bgcolor: '#FEF3C7' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="حذف">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(cat)}
                        sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={addOpen || !!editTarget}
        onClose={closeDialogs}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: FONT, borderBottom: '3px solid', borderImage: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%) 1' }}>
          {editTarget ? 'تعديل نوع الرسوم' : 'إضافة نوع رسوم جديد'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="اسم الرسوم"
            value={nameInput}
            onChange={(e) => { setNameInput(e.target.value); setNameError(''); }}
            error={!!nameError}
            helperText={nameError || 'مثال: رسوم الدراسة، رسوم الكتب، رسوم المختبر'}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialogs} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB', color: '#6B7280' }}>إلغاء</Button>
          <Button variant="contained" onClick={handleSave} sx={{ ...primaryBtnSx, px: 3 }}>حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#DC2626', fontFamily: FONT }}>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT, color: '#1E1B4B' }}>هل أنت متأكد من حذف "{deleteConfirm?.name}"؟ سيتم حذف جميع قواعد هذه الرسوم.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB', color: '#6B7280' }}>إلغاء</Button>
          <Button color="error" variant="contained" onClick={handleDelete} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, boxShadow: 'none', bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' } }}>حذف</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Tab 1 – Default Fees ────────────────────────────────────────────────────

function DefaultFeesTab() {
  const dispatch = useDispatch();
  const { defaultRules, loading, saving } = useSelector((s) => s.feesManagement);
  const [amounts, setAmounts] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    dispatch(fetchDefaultRules());
  }, [dispatch]);

  useEffect(() => {
    const map = {};
    defaultRules.forEach((r) => { map[r.category_id] = r.amount; });
    setAmounts(map);
  }, [defaultRules]);

  const handleSave = async () => {
    const items = defaultRules.map((r) => ({
      category_id: r.category_id,
      amount: parseFloat(amounts[r.category_id] || 0),
    }));
    await dispatch(saveDefaultRules(items));
    await dispatch(fetchDefaultRules());
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const total = defaultRules.reduce((sum, r) => sum + parseFloat(amounts[r.category_id] || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: FONT }}>الرسوم الافتراضية</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontFamily: FONT }}>
            هذه الرسوم تُطبَّق على جميع الطلاب الذين ليس لديهم رسوم خاصة بالصف أو بالطالب
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}
        >
          حفظ
        </Button>
      </Box>

      {saved && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>تم حفظ الرسوم الافتراضية بنجاح</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
      ) : defaultRules.length === 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2.5, fontFamily: FONT }}>لا توجد أنواع رسوم. أضف أنواعاً في تبويب "أنواع الرسوم" أولاً.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                <TableCell align="left" width={200} sx={headCellSx}>المبلغ (₪)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {defaultRules.map((r) => (
                <TableRow key={r.category_id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                  <TableCell>
                    <Typography fontWeight={600} component="span" sx={{ color: '#1E1B4B', fontFamily: FONT }}>{r.category_name}</Typography>
                    {!r.is_active && <Chip label="معطّل" size="small" sx={{ ml: 1, fontFamily: FONT }} />}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 10 }}
                      value={amounts[r.category_id] ?? 0}
                      onChange={(e) => setAmounts((prev) => ({ ...prev, [r.category_id]: e.target.value }))}
                      sx={{ width: 160 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: '#EEF2FF' }}>
                <TableCell><Typography fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: FONT }}>الإجمالي</Typography></TableCell>
                <TableCell><Typography fontWeight={800} sx={{ color: '#4338CA', fontFamily: FONT }}>{currency(total)}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Tab 2 – Class Fees ───────────────────────────────────────────────────────

function ClassFeesTab() {
  const dispatch = useDispatch();
  const { classRules, classDiscounts, loading, saving } = useSelector((s) => s.feesManagement);
  const classesList = useSelector((s) => s.classes.list);
  const [selectedClass, setSelectedClass] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [discountPct, setDiscountPct] = useState({});
  const [discountAmt, setDiscountAmt] = useState({});
  const [reasonType, setReasonType] = useState({});
  const [reasonNote, setReasonNote] = useState({});
  const [saved, setSaved] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState(0);

  useEffect(() => {
    dispatch(fetchClasses());
  }, [dispatch]);

  useEffect(() => {
    if (selectedClass) {
      dispatch(fetchClassRules(selectedClass.id));
      dispatch(fetchClassDiscounts(selectedClass.id));
    }
  }, [dispatch, selectedClass]);

  const rules = selectedClass ? (classRules[selectedClass.id] || []) : [];
  const discounts = selectedClass ? (classDiscounts[selectedClass.id] || []) : [];

  useEffect(() => {
    const amtMap = {};
    rules.forEach((r) => {
      if (r.has_override) amtMap[r.category_id] = r.amount;
    });
    setAmounts(amtMap);
  }, [rules]);

  useEffect(() => {
    const pctMap = {};
    const amtMap = {};
    const reasonTypeMap = {};
    const reasonNoteMap = {};
    discounts.forEach((d) => {
      pctMap[d.category_id] = d.discount_percentage;
      amtMap[d.category_id] = d.discount_amount;
      reasonTypeMap[d.category_id] = d.reason_type || '';
      reasonNoteMap[d.category_id] = d.reason_note || '';
    });
    setDiscountPct(pctMap);
    setDiscountAmt(amtMap);
    setReasonType(reasonTypeMap);
    setReasonNote(reasonNoteMap);
  }, [discounts]);

  const handleSaveFees = async () => {
    if (!selectedClass) return;
    const items = rules.map((r) => {
      const val = amounts[r.category_id];
      if (val === undefined || val === '' || val === null) {
        return { category_id: r.category_id, remove: true };
      }
      return { category_id: r.category_id, amount: parseFloat(val) };
    });
    await dispatch(saveClassRules({ classId: selectedClass.id, items }));
    await dispatch(fetchClassRules(selectedClass.id));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSaveDiscounts = async () => {
    if (!selectedClass) return;
    const items = rules.map((r) => ({
      category_id: r.category_id,
      discount_percentage: parseFloat(discountPct[r.category_id] || 0),
      discount_amount: parseFloat(discountAmt[r.category_id] || 0),
      reason_type: reasonType[r.category_id] || '',
      reason_note: reasonType[r.category_id] === 'other' ? (reasonNote[r.category_id] || '') : '',
    }));
    await dispatch(saveClassDiscounts({ classId: selectedClass.id, items }));
    await dispatch(fetchClassDiscounts(selectedClass.id));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} mb={1} sx={{ color: '#1E1B4B', fontFamily: FONT }}>رسوم الصفوف</Typography>
      <Typography variant="body2" mb={2} sx={{ color: '#6B7280', fontFamily: FONT }}>
        يمكنك تحديد رسوم مختلفة لكل صف. إذا لم تحدد قيمة لنوع رسوم معين، سيتم استخدام الرسوم الافتراضية.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
        <Autocomplete
          options={classesList || []}
          getOptionLabel={(o) => o.name || ''}
          value={selectedClass}
          onChange={(_, v) => setSelectedClass(v)}
          renderInput={(params) => <TextField {...params} label="اختر الصف" size="small" />}
        />
      </FormControl>

      {!selectedClass ? (
        <Alert severity="info" sx={{ borderRadius: 2.5, fontFamily: FONT }}>اختر صفاً لعرض وتعديل رسومه</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
      ) : (
        <>
          <Tabs
            value={activeSubTab}
            onChange={(_, v) => setActiveSubTab(v)}
            sx={{
              mb: 2,
              borderBottom: '1px solid #E5E7EB',
              '& .MuiTab-root': { fontWeight: 700, fontFamily: FONT, textTransform: 'none', color: '#6B7280' },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA' },
            }}
          >
            <Tab label="مبالغ الرسوم" />
            <Tab label="الخصومات" />
          </Tabs>

          {saved && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>تم الحفظ بنجاح</Alert>}

          {activeSubTab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>
                اتركه فارغاً للاستخدام الرسوم الافتراضية. أدخل قيمة لتجاوز الرسوم الافتراضية لهذا الصف.
              </Alert>
              <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                      <TableCell align="left" sx={headCellSx}>الرسوم الافتراضية</TableCell>
                      <TableCell align="left" sx={headCellSx}>رسوم الصف (تجاوز)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((r) => (
                      <TableRow key={r.category_id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                        <TableCell><Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{r.category_name}</Typography></TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#6B7280', fontFamily: FONT }}>{currency(r.default_amount)}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 10 }}
                            placeholder={`افتراضي: ${r.default_amount}`}
                            value={amounts[r.category_id] ?? ''}
                            onChange={(e) => setAmounts((p) => ({ ...p, [r.category_id]: e.target.value }))}
                            sx={{ width: 160 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveFees}
                  disabled={saving}
                  sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}
                >
                  حفظ رسوم الصف
                </Button>
              </Box>
            </>
          )}

          {activeSubTab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>
                يمكنك إضافة خصم (نسبة أو مبلغ ثابت) لكل نوع رسوم لجميع طلاب هذا الصف.
              </Alert>
              <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                      <TableCell align="left" sx={headCellSx}>نسبة الخصم (%)</TableCell>
                      <TableCell align="left" sx={headCellSx}>مبلغ الخصم (₪)</TableCell>
                      <TableCell align="left" sx={headCellSx}>سبب الخصم</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((r) => (
                      <TableRow key={r.category_id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                        <TableCell><Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{r.category_name}</Typography></TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            value={discountPct[r.category_id] ?? 0}
                            onChange={(e) => setDiscountPct((p) => ({ ...p, [r.category_id]: e.target.value }))}
                            sx={{ width: 120 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 10 }}
                            value={discountAmt[r.category_id] ?? 0}
                            onChange={(e) => setDiscountAmt((p) => ({ ...p, [r.category_id]: e.target.value }))}
                            sx={{ width: 140 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack useFlexGap direction="row" spacing={1}>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <Select
                                displayEmpty
                                value={reasonType[r.category_id] || ''}
                                onChange={(e) => setReasonType((p) => ({ ...p, [r.category_id]: e.target.value }))}
                              >
                                <MenuItem value=""><em>بدون سبب</em></MenuItem>
                                {DISCOUNT_REASONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {reasonType[r.category_id] === 'other' && (
                              <TextField
                                size="small"
                                placeholder="اذكر السبب"
                                value={reasonNote[r.category_id] || ''}
                                onChange={(e) => setReasonNote((p) => ({ ...p, [r.category_id]: e.target.value }))}
                                sx={{ minWidth: 160 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveDiscounts}
                  disabled={saving}
                  sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}
                >
                  حفظ الخصومات
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Tab 3 – Student Fees ─────────────────────────────────────────────────────

function StudentFeesTab() {
  const dispatch = useDispatch();
  const { studentFees, studentDiscounts, loading, saving } = useSelector((s) => s.feesManagement);
  const schoolYears = useSelector((s) => s.schoolYears.list);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [discountPct, setDiscountPct] = useState({});
  const [discountAmt, setDiscountAmt] = useState({});
  const [reasonType, setReasonType] = useState({});
  const [reasonNote, setReasonNote] = useState({});
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [saved, setSaved] = useState(false);

  const activeYear = schoolYears?.find((y) => y.is_active);

  useEffect(() => {
    dispatch(fetchSchoolYears());
  }, [dispatch]);

  useEffect(() => {
    if (activeYear && !selectedYear) setSelectedYear(activeYear);
  }, [activeYear]);

  // Search students
  useEffect(() => {
    if (!studentSearch || studentSearch.length < 2) { setStudentOptions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/students/`,
          { params: { search: studentSearch, limit: 20 } }
        );
        setStudentOptions(res.data?.results || res.data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [studentSearch]);

  const loadStudentData = useCallback(() => {
    if (!selectedStudent || !selectedYear) return;
    dispatch(fetchStudentFees({ studentId: selectedStudent.id, schoolYearId: selectedYear.id }));
    dispatch(fetchStudentDiscounts({ studentId: selectedStudent.id, schoolYearId: selectedYear.id }));
  }, [dispatch, selectedStudent, selectedYear]);

  useEffect(() => {
    dispatch(clearStudentFees());
    loadStudentData();
  }, [loadStudentData]);

  useEffect(() => {
    if (!studentFees) return;
    const map = {};
    studentFees.fee_items?.forEach((item) => {
      if (item.rule_scope === 'student') map[item.category_id] = item.amount;
    });
    setOverrides(map);
  }, [studentFees]);

  const feeItems = studentFees?.fee_items || [];

  useEffect(() => {
    if (!studentDiscounts) return;
    const pctMap = {};
    const amtMap = {};
    const reasonTypeMap = {};
    const reasonNoteMap = {};
    studentDiscounts.forEach((d) => {
      pctMap[d.category_id] = d.discount_percentage;
      amtMap[d.category_id] = d.discount_amount;
      reasonTypeMap[d.category_id] = d.reason_type || '';
      reasonNoteMap[d.category_id] = d.reason_note || '';
    });
    setDiscountPct(pctMap);
    setDiscountAmt(amtMap);
    setReasonType(reasonTypeMap);
    setReasonNote(reasonNoteMap);
  }, [studentDiscounts]);

  const handleSaveFees = async () => {
    if (!selectedStudent || !selectedYear || !studentFees) return;
    const items = studentFees.fee_items.map((item) => {
      const val = overrides[item.category_id];
      if (val === undefined || val === '') {
        return { category_id: item.category_id, remove: true };
      }
      return { category_id: item.category_id, amount: parseFloat(val) };
    });
    await dispatch(saveStudentFees({
      studentId: selectedStudent.id,
      schoolYearId: selectedYear.id,
      items,
    }));
    loadStudentData();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleSaveDiscounts = async () => {
    if (!selectedStudent || !selectedYear || !studentFees) return;
    const items = studentFees.fee_items.map((item) => ({
      category_id: item.category_id,
      discount_percentage: parseFloat(discountPct[item.category_id] || 0),
      discount_amount: parseFloat(discountAmt[item.category_id] || 0),
      reason_type: reasonType[item.category_id] || '',
      reason_note: reasonType[item.category_id] === 'other' ? (reasonNote[item.category_id] || '') : '',
    }));
    await dispatch(saveStudentDiscounts({
      studentId: selectedStudent.id,
      schoolYearId: selectedYear.id,
      items,
    }));
    loadStudentData();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} mb={1} sx={{ color: '#1E1B4B', fontFamily: FONT }}>رسوم الطلاب</Typography>
      <Typography variant="body2" mb={2} sx={{ color: '#6B7280', fontFamily: FONT }}>
        عرض الرسوم المحسوبة لكل طالب وتطبيق تجاوزات أو خصومات خاصة به.
      </Typography>

      {/* Student + Year selectors */}
      <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <Autocomplete
          sx={{ minWidth: 280 }}
          options={studentOptions}
          getOptionLabel={(o) => `${o.first_name} ${o.second_name}${o.student_id ? ` (${o.student_id})` : ''}`}
          value={selectedStudent}
          onChange={(_, v) => setSelectedStudent(v)}
          onInputChange={(_, v) => setStudentSearch(v)}
          filterOptions={(x) => x}
          renderInput={(params) => <TextField {...params} label="ابحث عن طالب" size="small" />}
          noOptionsText={studentSearch.length < 2 ? 'اكتب للبحث...' : 'لا توجد نتائج'}
        />
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>السنة الدراسية</InputLabel>
          <Select
            label="السنة الدراسية"
            value={selectedYear?.id || ''}
            onChange={(e) => {
              const yr = schoolYears?.find((y) => y.id === e.target.value);
              setSelectedYear(yr || null);
            }}
          >
            {(schoolYears || []).map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.label} {y.is_active ? '(نشطة)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {!selectedStudent ? (
        <Alert severity="info" sx={{ borderRadius: 2.5, fontFamily: FONT }}>ابحث واختر طالباً لعرض رسومه</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
      ) : !studentFees ? null : (
        <>
          {/* Summary card */}
          <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 3 }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>الرسوم الإجمالية قبل الخصم</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{currency(studentFees.total_before_discount || 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>إجمالي الخصم</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#DC2626', fontFamily: FONT }}>{currency(studentFees.total_discount || 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>الرسوم الصافية</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#16A34A', fontFamily: FONT }}>{currency(studentFees.total_after_discount || 0)}</Typography>
              </Box>
            </Stack>
          </Paper>

          <Tabs
            value={activeSubTab}
            onChange={(_, v) => setActiveSubTab(v)}
            sx={{
              mb: 2,
              borderBottom: '1px solid #E5E7EB',
              '& .MuiTab-root': { fontWeight: 700, fontFamily: FONT, textTransform: 'none', color: '#6B7280' },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA' },
            }}
          >
            <Tab label="تجاوز المبالغ" />
            <Tab label="خصومات خاصة" />
          </Tabs>

          {saved && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>تم الحفظ بنجاح</Alert>}

          {activeSubTab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>
                اتركه فارغاً لاستخدام رسوم الصف/الافتراضية. أدخل قيمة لتطبيق رسوم خاصة بهذا الطالب.
              </Alert>
              <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                      <TableCell sx={headCellSx}>الصافي بعد الخصم</TableCell>
                      <TableCell sx={headCellSx}>المصدر</TableCell>
                      <TableCell sx={headCellSx}>تجاوز (خاص بالطالب)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feeItems.map((item) => (
                      <TableRow key={item.category_id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                        <TableCell><Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{item.category_name}</Typography></TableCell>
                        <TableCell>
                          {item.total_discount > 0 && (
                            <Typography variant="caption" sx={{ color: '#DC2626', display: 'block', textDecoration: 'line-through', fontFamily: FONT }}>
                              {currency(item.amount)}
                            </Typography>
                          )}
                          <Typography fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: FONT }}>
                            {currency(item.final_amount ?? item.amount)}
                          </Typography>
                          {item.total_discount > 0 && (
                            <Typography variant="caption" sx={{ color: '#DC2626', fontFamily: FONT }}>
                              خصم {currency(item.total_discount)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.rule_scope === 'student' ? 'خاص بالطالب' : item.rule_scope === 'class' ? 'رسوم الصف' : 'افتراضي'}
                            color={item.rule_scope === 'student' ? 'primary' : item.rule_scope === 'class' ? 'info' : 'default'}
                            sx={{ fontWeight: 700, fontFamily: FONT }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 10 }}
                            placeholder={`محسوب: ${item.final_amount ?? item.amount}`}
                            value={overrides[item.category_id] ?? ''}
                            onChange={(e) => setOverrides((p) => ({ ...p, [item.category_id]: e.target.value }))}
                            sx={{ width: 160 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveFees}
                  disabled={saving}
                  sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}
                >
                  حفظ تجاوزات الطالب
                </Button>
              </Box>
            </>
          )}

          {activeSubTab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5, fontFamily: FONT }}>
                خصومات خاصة بهذا الطالب لكل نوع رسوم. تُضاف إلى خصومات الصف إن وجدت.
              </Alert>
              <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                      <TableCell sx={headCellSx}>نسبة الخصم (%)</TableCell>
                      <TableCell sx={headCellSx}>مبلغ الخصم (₪)</TableCell>
                      <TableCell sx={headCellSx}>الخصم المحسوب</TableCell>
                      <TableCell sx={headCellSx}>سبب الخصم</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feeItems.map((item) => {
                      const pct = parseFloat(discountPct[item.category_id] || 0);
                      const amt = parseFloat(discountAmt[item.category_id] || 0);
                      const calcDisc = (item.amount * pct / 100) + amt;
                      return (
                        <TableRow key={item.category_id} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                          <TableCell><Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{item.category_name}</Typography></TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, max: 100, step: 1 }}
                              value={discountPct[item.category_id] ?? 0}
                              onChange={(e) => setDiscountPct((p) => ({ ...p, [item.category_id]: e.target.value }))}
                              sx={{ width: 120 }}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, step: 10 }}
                              value={discountAmt[item.category_id] ?? 0}
                              onChange={(e) => setDiscountAmt((p) => ({ ...p, [item.category_id]: e.target.value }))}
                              sx={{ width: 140 }}
                              InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: calcDisc > 0 ? '#DC2626' : '#6B7280', fontWeight: calcDisc > 0 ? 700 : 400, fontFamily: FONT }}>
                              {calcDisc > 0 ? `-${currency(calcDisc)}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                  displayEmpty
                                  value={reasonType[item.category_id] || ''}
                                  onChange={(e) => setReasonType((p) => ({ ...p, [item.category_id]: e.target.value }))}
                                >
                                  <MenuItem value=""><em>بدون سبب</em></MenuItem>
                                  {DISCOUNT_REASONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {reasonType[item.category_id] === 'other' && (
                                <TextField
                                  size="small"
                                  placeholder="اذكر السبب"
                                  value={reasonNote[item.category_id] || ''}
                                  onChange={(e) => setReasonNote((p) => ({ ...p, [item.category_id]: e.target.value }))}
                                  sx={{ minWidth: 160 }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveDiscounts}
                  disabled={saving}
                  sx={{ ...primaryBtnSx, px: 2.5, py: 1 }}
                >
                  حفظ خصومات الطالب
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Tab 4 – Discounted Students ───────────────────────────────────────────────

const SOURCE_LABELS = {
  class: { label: 'رسوم الصف', color: 'info' },
  student: { label: 'خاص بالطالب', color: 'primary' },
  both: { label: 'صف + طالب', color: 'secondary' },
};

function DiscountedStudentsTab() {
  const dispatch = useDispatch();
  const { discountedStudents, categories, loading } = useSelector((s) => s.feesManagement);
  const classesList = useSelector((s) => s.classes.list);
  const schoolYears = useSelector((s) => s.schoolYears.list);
  const activeYear = schoolYears?.find((y) => y.is_active);

  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [minPct, setMinPct] = useState('');
  const [maxPct, setMaxPct] = useState('');

  useEffect(() => {
    dispatch(fetchClasses());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSchoolYears());
  }, [dispatch]);

  useEffect(() => {
    if (activeYear && !selectedYear) setSelectedYear(activeYear);
  }, [activeYear]);

  useEffect(() => {
    if (!selectedYear) return;
    dispatch(fetchDiscountedStudents({
      school_year_id: selectedYear.id,
      school_class_id: selectedClass?.id,
      category_id: selectedCategory?.id,
      min_percentage: minPct !== '' ? minPct : undefined,
      max_percentage: maxPct !== '' ? maxPct : undefined,
    }));
  }, [dispatch, selectedYear, selectedClass, selectedCategory, minPct, maxPct]);

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} mb={1} sx={{ color: '#1E1B4B', fontFamily: FONT }}>الطلاب ذوو الخصم</Typography>
      <Typography variant="body2" mb={2} sx={{ color: '#6B7280', fontFamily: FONT }}>
        قائمة بجميع الطلاب الذين لديهم خصم على الرسوم (سواء من خصم الصف أو خصم خاص بالطالب)، مع إمكانية التصفية.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <Autocomplete
          sx={{ minWidth: 220 }}
          options={classesList || []}
          getOptionLabel={(o) => o.name || ''}
          value={selectedClass}
          onChange={(_, v) => setSelectedClass(v)}
          renderInput={(params) => <TextField {...params} label="الصف" size="small" />}
        />
        <Autocomplete
          sx={{ minWidth: 220 }}
          options={categories || []}
          getOptionLabel={(o) => o.name || ''}
          value={selectedCategory}
          onChange={(_, v) => setSelectedCategory(v)}
          renderInput={(params) => <TextField {...params} label="نوع الرسوم" size="small" />}
        />
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>السنة الدراسية</InputLabel>
          <Select
            label="السنة الدراسية"
            value={selectedYear?.id || ''}
            onChange={(e) => {
              const yr = schoolYears?.find((y) => y.id === e.target.value);
              setSelectedYear(yr || null);
            }}
          >
            {(schoolYears || []).map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.label} {y.is_active ? '(نشطة)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="number"
          label="أقل نسبة خصم (%)"
          inputProps={{ min: 0, max: 100 }}
          value={minPct}
          onChange={(e) => setMinPct(e.target.value)}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="number"
          label="أعلى نسبة خصم (%)"
          inputProps={{ min: 0, max: 100 }}
          value={maxPct}
          onChange={(e) => setMaxPct(e.target.value)}
          sx={{ width: 160 }}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#4338CA' }} /></Box>
      ) : discountedStudents.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2.5, fontFamily: FONT }}>لا يوجد طلاب لديهم خصم وفق معايير التصفية الحالية.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>الطالب</TableCell>
                <TableCell sx={headCellSx}>الصف</TableCell>
                <TableCell sx={headCellSx}>نوع الرسوم</TableCell>
                <TableCell sx={headCellSx}>نسبة الخصم</TableCell>
                <TableCell sx={headCellSx}>مبلغ الخصم</TableCell>
                <TableCell sx={headCellSx}>الصافي بعد الخصم</TableCell>
                <TableCell sx={headCellSx}>المصدر</TableCell>
                <TableCell sx={headCellSx}>السبب</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {discountedStudents.map((row) => (
                <TableRow key={`${row.student_id}-${row.category_id}`} hover sx={{ '&:hover': { bgcolor: '#F8F9FF' } }}>
                  <TableCell>
                    <Typography fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>{row.student_name}</Typography>
                    {row.student_code && (
                      <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>{row.student_code}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>{row.school_class_name || '—'}</Typography></TableCell>
                  <TableCell><Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>{row.category_name}</Typography></TableCell>
                  <TableCell><Typography sx={{ color: '#DC2626', fontWeight: 700, fontFamily: FONT }}>{row.discount_percentage}%</Typography></TableCell>
                  <TableCell><Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>{currency(row.discount_amount)}</Typography></TableCell>
                  <TableCell><Typography fontWeight={700} sx={{ color: '#16A34A', fontFamily: FONT }}>{currency(row.final_amount)}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={SOURCE_LABELS[row.source]?.label || row.source}
                      color={SOURCE_LABELS[row.source]?.color || 'default'}
                      sx={{ fontWeight: 700, fontFamily: FONT }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>
                      {row.reason_type ? reasonLabel(row.reason_type) : '—'}
                      {row.reason_type === 'other' && row.reason_note ? `: ${row.reason_note}` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const FeeManagementPage = () => {
  const dispatch = useDispatch();
  const { error } = useSelector((s) => s.feesManagement);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    dispatch(fetchFeeCategories());
    dispatch(fetchDefaultRules());
  }, [dispatch]);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Page Header (gradient) */}
        <MotionPaper
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountBalanceWalletIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                إدارة الرسوم المدرسية
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
              >
                أنشئ وعدّل أنواع الرسوم وحدد المبالغ الافتراضية أو الخاصة بالصف أو بالطالب، مع إمكانية الخصومات
              </Typography>
            </Box>
          </Stack>
        </MotionPaper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2.5, fontFamily: FONT }}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}

        <MotionPaper
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{ ...cardSx, mb: 3, overflow: 'hidden' }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #E5E7EB',
              px: 1,
              '& .MuiTab-root': {
                minHeight: 56,
                fontWeight: 700,
                fontFamily: FONT,
                textTransform: 'none',
                color: '#6B7280',
              },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA', height: 3, borderRadius: 3 },
            }}
          >
            {TABS.map((t, i) => (
              <Tab
                key={i}
                label={t.label}
                icon={t.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {tab === 0 && <CategoriesTab />}
            {tab === 1 && <DefaultFeesTab />}
            {tab === 2 && <ClassFeesTab />}
            {tab === 3 && <StudentFeesTab />}
            {tab === 4 && <DiscountedStudentsTab />}
          </Box>
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default FeeManagementPage;
